import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Deck } from "@/common/decks";
import { updateDeck } from "@/lib/serverComm";

interface DeckError {
  message: string;
  type: "fetch" | "delete" | "update";
}

interface UseDeckActionsProps {
  onDeckUpdate: (updatedDeck: Deck) => void;
  onError: (error: DeckError) => void;
  onDeleteRequest: (deckId: string) => void;
  onDropdownToggle: (deckId: string | undefined) => void;
}

export const useDeckActions = ({
  onDeckUpdate,
  onError,
  onDeleteRequest,
  onDropdownToggle,
}: UseDeckActionsProps) => {
  const navigate = useNavigate();

  const handleNavigate = useCallback(
    (deckId: string | undefined) => {
      if (deckId) {
        navigate(`/decks/${deckId}`);
      }
    },
    [navigate],
  );

  const handleTogglePin = useCallback(
    async (deck: Deck) => {
      if (!deck.id) return;

      try {
        const updatedDeck = { ...deck, pinned: !deck.pinned };
        await updateDeck(updatedDeck);
        onDeckUpdate(updatedDeck);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update deck";
        onError({ message: errorMessage, type: "update" });
        console.error("Error toggling pin:", err);
      }
    },
    [onDeckUpdate, onError],
  );

  const handleDelete = useCallback(
    (deckId: string) => {
      onDeleteRequest(deckId);
    },
    [onDeleteRequest],
  );

  const handleDropdownToggle = useCallback(
    (deckId: string | undefined) => {
      onDropdownToggle(deckId);
    },
    [onDropdownToggle],
  );

  return {
    onNavigate: handleNavigate,
    onTogglePin: handleTogglePin,
    onDelete: handleDelete,
    onToggleDropdown: handleDropdownToggle,
  };
};
